// TAS-126: single source of truth for step-type → in-game route.
//
// This map was duplicated in GameWrapper (the GM's local navigation) and
// useGameEvents (followers' broadcast-driven navigation), and the two drifted:
//   - 'game-rules' 404'd for followers (routed to a nonexistent /game/game-rules)
//     while the GM silently skipped it;
//   - 'prize' navigated the GM but returned null for followers, so they never
//     followed the room to the prize screen;
//   - 'group-review' dropped the &wines= list for followers.
// Both consumers now derive the path here. Each keeps its OWN handling of a
// null return: the GM auto-advances a pass-through step, followers simply wait
// for the next real navigation.
//
// Returns the route string, or null for pass-through / unknown step types that
// have no dedicated screen — 'game-rules' and the legacy 'ability' steps are
// pass-throughs the GM advances past (there is no rules/ability route).
export function stepTypeToRoute(step, { sessionCode, gameType, hideWineDetails, wineList } = {}) {
  if (!step || !sessionCode) return null;
  const { type, wine, index, category } = step;
  const wines = Array.isArray(wineList) ? wineList.join(',') : '';

  switch (type) {
    case 'intro':
      return `/game/intro?code=${sessionCode}${category ? `&category=${category}` : ''}${wine ? `&wine=${wine}` : ''}&gameType=${gameType}`;
    case 'wine-intro':
      return `/game/intro?code=${sessionCode}&wine=${wine}&hideDetails=${hideWineDetails}`;
    case 'question':
      return `/game?code=${sessionCode}&wine=${wine}`;
    case 'reveal':
    case 'reveal-answer':
      return `/game/reveal?code=${sessionCode}&wine=${wine}&question=${index}`;
    case 'discussion':
      return `/game/discussion?code=${sessionCode}&wine=${wine}&question=${index}`;
    case 'results':
      return `/game/results?code=${sessionCode}&wine=${wine}&question=${index}`;
    case 'scoreboard':
      return `/game/scoreboard?code=${sessionCode}${wine ? `&wine=${wine}` : ''}`;
    case 'participation-mode':
      return `/game/participation-mode?code=${sessionCode}`;
    case 'game-view-mode':
      return `/game/game-view-mode?code=${sessionCode}`;
    case 'final-leaderboard':
      return `/game/final-leaderboard?code=${sessionCode}`;
    case 'wine-review':
      return `/game/review?code=${sessionCode}&wine=${wine}&hideDetails=${hideWineDetails}`;
    case 'wine-rating-reveal':
      return `/game/wine-rating-reveal?code=${sessionCode}&wine=${wine}`;
    case 'wine-reveal':
      return `/game/wine-reveal?code=${sessionCode}&wine=${wine}`;
    case 'group-review':
      return `/game/group-review?code=${sessionCode}${wine ? `&wine=${wine}` : ''}&wines=${wines}`;
    case 'prize':
      return `/game/prize?code=${sessionCode}`;
    case 'thank-you':
      return `/game/thank-you?code=${sessionCode}`;
    default:
      // Pass-through (game-rules, legacy ability) or unknown — no screen.
      return null;
  }
}

// Steps the room can sit on that have no screen anywhere: the GM's phone
// auto-advances past them (stepTypeToRoute returns null for them). A known list,
// not "any type without a route": an old TV tab meeting a newer step type must
// not hold the last screen for a whole real step. Pre-game types are not here.
const PASS_THROUGH_STEP_TYPES = new Set(['ability']);
export const isPassThroughStepType = (type) => PASS_THROUGH_STEP_TYPES.has(type);

// TAS-471: the steps after the podium that each player reaches on their own.
// FinalLeaderboard's Continue (and the prize page's) move ONE phone, locally,
// and write `tasted_step_<code>`; the room's step stays on the podium unless a
// TV-mode GM moves it. So a full reload on the thanks page (a Yoco return, a
// plain refresh) hears "podium" from the server and used to snap the phone
// back there.
const PER_PLAYER_STEP_TYPES = new Set(['prize', 'thank-you']);

/**
 * The step a reloading phone should resume on, given the room's step and this
 * device's cached step for the same session.
 *
 * The cache wins only when the room is at or past the final leaderboard and
 * every step from the room's up to the cached one is a per-player step (prize,
 * thank-you). Those reveal nothing the table has not seen, so TAS-25's rule
 * still holds: a client-written cache can never move a phone past any step
 * before the podium. Anything else returns the room's step unchanged.
 *
 * @param {Array<{type?: string}>} steps  the session's flow steps
 * @param {number} roomIndex  the authoritative step (live row or gm-next)
 * @param {number} cachedIndex  this device's `tasted_step_<code>`, or -1/0 when none
 * @returns {number}
 */
export function perPlayerResumeStep(steps, roomIndex, cachedIndex) {
  if (!Array.isArray(steps) || !Number.isInteger(roomIndex) || !Number.isInteger(cachedIndex)) return roomIndex;
  if (roomIndex < 0 || cachedIndex <= roomIndex || cachedIndex >= steps.length) return roomIndex;
  const podium = steps.findIndex((s) => s?.type === 'final-leaderboard');
  if (podium === -1 || roomIndex < podium) return roomIndex;
  for (let i = roomIndex + 1; i <= cachedIndex; i += 1) {
    if (!PER_PLAYER_STEP_TYPES.has(steps[i]?.type)) return roomIndex;
  }
  return cachedIndex;
}
